import { useCallback, useEffect, useRef } from 'react';

type ExecuteFunction = (
	variables?: any,
	options?: { bypassCache?: boolean; skipLoadingState?: boolean },
) => void | Promise<void>;
type FetchPolicy = 'cache-first' | 'network-only' | 'cache-and-network' | 'no-cache';

export function usePollInterval(
	pollInterval: number | undefined,
	execute: ExecuteFunction,
	fetchPolicy?: FetchPolicy,
): { stopPolling: () => void } {
	if (pollInterval && fetchPolicy === 'cache-first') {
		throw new Error(
			'Queries that specify the cache-first fetchPolicy cannot also be polling queries.',
		);
	}

	const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

	const stopPolling = useCallback(() => {
		if (pollIntervalRef.current) {
			clearInterval(pollIntervalRef.current);
			pollIntervalRef.current = null;
		}
	}, []);

	const handlePoll = useCallback(() => {
		void execute(undefined, { bypassCache: true, skipLoadingState: true });
	}, [execute]);

	useEffect(() => {
		// Clear any existing poll interval
		if (pollIntervalRef.current) {
			clearInterval(pollIntervalRef.current);
			pollIntervalRef.current = null;
		}

		if (pollInterval && pollInterval > 0) {
			pollIntervalRef.current = setInterval(() => {
				handlePoll();
			}, pollInterval);
		}

		// Cleanup function to clear interval on unmount or when pollInterval changes
		return () => {
			if (pollIntervalRef.current) {
				clearInterval(pollIntervalRef.current);
				pollIntervalRef.current = null;
			}
		};
	}, [pollInterval, handlePoll]);

	return { stopPolling };
}
